import { useState, useEffect } from 'react';
import {
  Container,
  StepTitle,
  Footer,
  ProposalDetails,
  DetailItem,
  AcceptanceText,
  DocumentViewer,
  DocumentLinkButton,
} from "./styles";
import { SolidButton } from '../../../../../../components/SolidButton';

interface IStepZeroProps {
  proposal: IBusinessProposalDTO | null;
  nextStep: () => void;
  loading: boolean;
}

export function StepZero({ proposal, nextStep, loading }: IStepZeroProps) {
  const [htmlContent, setHtmlContent] = useState('');
  const [isDocumentLoading, setIsDocumentLoading] = useState(true);
  const [isDocumentVisible, setIsDocumentVisible] = useState(false);

  useEffect(() => {
    if (proposal?.proposal_document_url) {
      setIsDocumentLoading(true);
      const fullDocumentUrl = `${process.env.REACT_APP_STORAGE_BASE_URL}/${proposal.proposal_document_url}`;

      fetch(fullDocumentUrl)
        .then((response) => response.text())
        .then((html) => {
          setHtmlContent(html);
        })
        .catch((err) => {
          console.error("Erro ao carregar o documento da proposta:", err);
          setHtmlContent("<p>Não foi possível carregar o documento.</p>");
        })
        .finally(() => {
          setIsDocumentLoading(false);
        });
    }
  }, [proposal]);


  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return "N/A";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const formatNumber = (value?: number) => {
    if (value === undefined || value === null) return "N/A";
    return value.toLocaleString('pt-BR');
  };

  return (
    <Container>
      <StepTitle>Revisão da Proposta Comercial</StepTitle>

      <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}>
        Olá, {proposal?.licensee_name}! Por favor, revise os detalhes e o documento da sua proposta abaixo.
      </p>

      <ProposalDetails>
        <DetailItem>
          <strong>Nome do Licenciado:</strong>
          <span>{proposal?.licensee_name}</span>
        </DetailItem>
        <DetailItem>
          <strong>Valor Total Pago:</strong>
          <span>{formatCurrency(proposal?.total_paid)}</span>
        </DetailItem>
        <DetailItem>
          <strong>FixCoins a Receber:</strong>
          <span>{formatNumber(proposal?.fixcoins_to_add)}</span>
        </DetailItem>
        <DetailItem>
          <strong>Método de Pagamento:</strong>
          <span>{proposal?.payment_method || 'Não especificado'}</span>
        </DetailItem>
      </ProposalDetails>

      {isDocumentVisible &&
        <DocumentViewer>
          {isDocumentLoading ? (
            <p>Carregando documento...</p>
          ) : (
            <iframe
              srcDoc={htmlContent}
              width="100%"
              height="100%"
              frameBorder="0"
              title="Visualizador de Proposta Comercial"
              onLoad={() => {
                const iframe = document.getElementById("proposalFrame") as HTMLIFrameElement;
                if (iframe && iframe.contentWindow) {
                  const setHeight = () => {
                    const body = iframe.contentWindow?.document.body;
                    const html = iframe.contentWindow?.document.documentElement;
                    if (!body || !html) return;

                    const height = Math.max(
                      body.scrollHeight, body.offsetHeight,
                      html.clientHeight, html.scrollHeight, html.offsetHeight
                    );
                    iframe.style.height = `${height}px`;
                  };

                  setHeight();

                  // Observa mudanças no corpo do iframe para reajustar a altura
                  const resizeObserver = new ResizeObserver(setHeight);
                  resizeObserver.observe(iframe.contentWindow.document.body);
                }
              }}
            ></iframe>
          )}
        </DocumentViewer>
      }

      {proposal?.proposal_document_url && (
        <DocumentLinkButton type='button' onClick={() => setIsDocumentVisible(!isDocumentVisible)}>
          {isDocumentVisible ? 'Ocultar Documento' : 'Visualizar Documento da Proposta'}
        </DocumentLinkButton>
      )}

      <Footer>
        <SolidButton
          text="Li e Aceito a Proposta, Continuar"
          buttonWidth={300}
          buttonHeight={42}
          onClick={nextStep}
          loading={loading}
          disabled={loading || isDocumentLoading}
        />
        <AcceptanceText>
          Ao prosseguir, você declara que leu e concorda com os termos da proposta apresentada no documento.
        </AcceptanceText>
      </Footer>
    </Container>
  );
}
